// Warnings for a device that has been offline for a while (docs/plans/offline-safety.md):
// the 3-day lock on money and stock, changes waiting too long, and the
// per-business counts sign-out reads.
import { Banner, Button, useToast } from '@ims/ui'
import { RefreshCw } from '@ims/ui/icons'
import { useEffect, useState } from 'react'
import { errorMessage, useSyncState } from '../hooks.ts'
import { offlineLock, recordUnsynced, unsyncedTooLong } from '../lib/offline.ts'
import { useUser } from '../session.tsx'
import { useStore, useWorkspace } from '../workspace.tsx'
import { useStorageAdvice } from './storage.tsx'

function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])
  return now
}

/**
 * Keeps this business's count of unsynced changes where sign-out can read it,
 * and asks before the tab closes while changes have waited too long (they stay
 * on this device, but nobody else sees them until it opens again online).
 */
export function useTrackUnsynced(): void {
  const user = useUser()
  const { current } = useWorkspace()
  const sync = useSyncState()
  const now = useMinuteClock()
  const count = sync.pending + sync.failed.length
  const tenantId = current?.tenant_id
  useEffect(() => {
    if (tenantId) recordUnsynced(user.id, tenantId, count)
  }, [user.id, tenantId, count])
  const stuck = unsyncedTooLong(sync.oldestUnsyncedAt, now)
  useEffect(() => {
    if (!stuck) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [stuck])
}

/** useTrackUnsynced in a component of its own, so sync changes don't re-render the shell. */
export function UnsyncedTracker(): null {
  useTrackUnsynced()
  return null
}

function SyncNowButton() {
  const { engine } = useStore()
  const toast = useToast()
  return (
    <Button
      size="sm"
      variant="secondary"
      icon={<RefreshCw className="size-4" />}
      onClick={() => void engine.syncNow().catch((e: unknown) => toast.error(errorMessage(e)))}
    >
      Sync now
    </Button>
  )
}

export function OfflineBanners() {
  const sync = useSyncState()
  const advice = useStorageAdvice()
  // Re-render every minute, but read the time now: a check-in newer than a
  // minute-old clock would otherwise look like a clock moved back.
  useMinuteClock()
  const now = new Date()
  const lock = offlineLock(sync.lastCheckinAt, now)
  return (
    <>
      {lock.kind === 'stale' && (
        <Banner tone="danger" action={<SyncNowButton />}>
          This device hasn’t synced for 3 days, so billing, payments and stock changes are paused
          until it does. Orders and drafts still work. Connect to the internet and sync.
        </Banner>
      )}
      {lock.kind === 'soon' && (
        <Banner tone="warning" action={<SyncNowButton />}>
          This device hasn’t synced for 2 days. Connect to the internet within {lock.hoursLeft} hour
          {lock.hoursLeft === 1 ? '' : 's'} to keep billing.
        </Banner>
      )}
      {/* The storage banner already says it when storage is unprotected. */}
      {lock.kind === 'ok' &&
        advice !== 'at-risk' &&
        unsyncedTooLong(sync.oldestUnsyncedAt, now) && (
          <Banner tone="warning" action={<SyncNowButton />}>
            Some changes haven’t reached the server for over 30 minutes. They are safe on this
            device: don’t uninstall the app or clear browser data until they sync.
          </Banner>
        )}
    </>
  )
}
